import * as React from "react"
import { ChevronLeft, ChevronRight, Copy, Pencil, Trash2 } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { SortableHeader } from "@/components/shared/sortable-header"
import {
  ApiErrorState,
  ClientNotFoundState,
  NoDevicesEmpty,
  NoSearchResultsEmpty,
} from "@/components/shared/empty-states"
import { DevicesTableSkeleton } from "@/components/shared/loading-skeletons"
import { DeleteAlfredDeviceDialog } from "@/components/alfred/delete-alfred-device-dialog"
import { BulkSelectionToolbar } from "@/components/alfred/bulk-selection-toolbar"
import { ArrayFieldCell, ArrayHoverList } from "@/components/alfred/array-field-cell"
import { ProVenueInstanceEditor } from "@/components/alfred/pro-venue-instance-editor"
import { DisplayConfigurationEditor } from "@/components/alfred/display-configuration-editor"
import { AppleVendorEditor } from "@/components/alfred/apple-vendor-editor"
import { GoogleVendorEditor } from "@/components/alfred/google-vendor-editor"
import { DISPLAY_CONFIGURATION_ITEM_OPTIONS } from "@/types/alfred-config"
import {
  isAlfredDeviceSortField,
  type AlfredDeviceSortField,
  type AlfredDeviceUI,
  type SortOrder,
} from "@/types/alfred-device"

export type ColumnKey =
  | "sotiDeviceId"
  | "venueCode"
  | "facilityCode"
  | "league"
  | "proVenueUser"
  | "proVenueInstances"
  | "operatingMode"
  | "areaCode"
  | "deviceCode"
  | "validationVendor"
  | "ticketmasterVenueId"
  | "offlineValidationMode"
  | "offlineBatchUploadLimit"
  | "turnstileMode"
  | "adminModeIdleWindow"
  | "validDisplayTime"
  | "invalidDisplayTime"
  | "unlockedAccessTime"
  | "soundValidNumber"
  | "soundInvalidNumber"
  | "soundWarningNumber"
  | "maxCpuTemperature"
  | "minCpuTemperature"
  | "nfcEnabled"
  | "exitScanningAllowed"
  | "isLockFailSafe"
  | "useSimpleResponse"
  | "enableFan"
  | "enableHotspot"
  | "wakeLockEnabled"
  | "enableScanReset"
  | "enableManualValidationMask"
  | "displayConfiguration"
  | "appleVendors"
  | "googleVendors"
  | "mainBackgroundColor"
  | "secondaryBackgroundColor"
  | "idleStateColor"
  | "createdAt"
  | "updatedAt"
  | "updatedBy"

export const OPTIONAL_COLUMNS: { key: ColumnKey; label: string }[] = [
  { key: "sotiDeviceId",            label: "SOTI Device ID" },
  { key: "venueCode",               label: "Venue Code" },
  { key: "facilityCode",            label: "Facility Code" },
  { key: "league",                  label: "League" },
  { key: "proVenueUser",            label: "ProVenue User" },
  { key: "proVenueInstances",       label: "ProVenue Instances" },
  { key: "operatingMode",           label: "Operating Mode" },
  { key: "areaCode",                label: "Area Code" },
  { key: "deviceCode",              label: "Device Code" },
  { key: "validationVendor",        label: "Validation Vendor" },
  { key: "ticketmasterVenueId",     label: "TM Venue ID" },
  { key: "offlineValidationMode",   label: "Offline Validation" },
  { key: "offlineBatchUploadLimit", label: "Offline Batch Limit" },
  { key: "turnstileMode",           label: "Turnstile Mode" },
  { key: "adminModeIdleWindow",     label: "Admin Idle Window" },
  { key: "validDisplayTime",        label: "Valid Display Time" },
  { key: "invalidDisplayTime",      label: "Invalid Display Time" },
  { key: "unlockedAccessTime",      label: "Unlocked Access Time" },
  { key: "soundValidNumber",        label: "Sound Valid #" },
  { key: "soundInvalidNumber",      label: "Sound Invalid #" },
  { key: "soundWarningNumber",      label: "Sound Warning #" },
  { key: "maxCpuTemperature",       label: "Max CPU Temp" },
  { key: "minCpuTemperature",       label: "Min CPU Temp" },
  { key: "nfcEnabled",              label: "NFC" },
  { key: "exitScanningAllowed",     label: "Exit Scanning" },
  { key: "isLockFailSafe",          label: "Lock Fail Safe" },
  { key: "useSimpleResponse",       label: "Simple Response" },
  { key: "enableFan",               label: "Fan" },
  { key: "enableHotspot",           label: "Hotspot" },
  { key: "wakeLockEnabled",         label: "Wake Lock" },
  { key: "enableScanReset",         label: "Scan Reset" },
  { key: "enableManualValidationMask", label: "Manual Validation Mask" },
  { key: "displayConfiguration",    label: "Display Configuration" },
  { key: "appleVendors",            label: "Apple Vendors" },
  { key: "googleVendors",           label: "Google Vendors" },
  { key: "mainBackgroundColor",     label: "Main BG Color" },
  { key: "secondaryBackgroundColor",label: "Secondary BG Color" },
  { key: "idleStateColor",          label: "Idle State Color" },
  { key: "createdAt",               label: "Created At" },
  { key: "updatedAt",               label: "Last Updated" },
  { key: "updatedBy",               label: "Updated By" },
]

export const DEFAULT_VISIBLE_COLUMNS = new Set<ColumnKey>([
  "sotiDeviceId", "venueCode", "operatingMode", "areaCode", "deviceCode",
  "validationVendor", "nfcEnabled", "exitScanningAllowed", "updatedAt", "updatedBy",
])

interface AlfredDevicesTableProps {
  devices: AlfredDeviceUI[]
  isLoading?: boolean
  error?: Error | null
  isSelectedClientNotFound?: boolean
  onRetry?: () => void
  onClearClientFilter?: () => void
  onAddDevice?: () => void
  onRowClick?: (device: AlfredDeviceUI) => void
  onEdit?: (device: AlfredDeviceUI) => void
  onClone?: (device: AlfredDeviceUI) => void
  onDelete?: (device: AlfredDeviceUI) => void | Promise<void>
  sortBy: AlfredDeviceSortField | null
  sortOrder: SortOrder
  onSort: (field: AlfredDeviceSortField) => void
  hasActiveFilter?: boolean
  onClearFilters?: () => void
  selectedIds?: Set<number>
  onToggleSelect?: (id: number) => void
  onToggleAll?: (ids: number[], checked: boolean) => void
  onBulkEdit: () => void
  onBulkDelete: () => void
  onClearSelection: () => void
  bulkEditor?: React.ReactNode
  total?: number
  page?: number
  pageSize?: number
  onPageChange?: (page: number) => void
  onPageSizeChange?: (pageSize: number) => void
  visibleColumns?: Set<ColumnKey>
  isDeleting?: boolean
  isBulkDeleting?: boolean
  isInteractionDisabled?: boolean
}

export function AlfredDevicesTable({
  devices,
  isLoading,
  error,
  isSelectedClientNotFound = false,
  onRetry,
  onClearClientFilter,
  onAddDevice,
  onRowClick,
  onEdit,
  onClone,
  onDelete,
  sortBy,
  sortOrder,
  onSort: onSortRequested,
  hasActiveFilter = false,
  onClearFilters,
  selectedIds = new Set(),
  onToggleSelect,
  onToggleAll,
  onBulkEdit,
  onBulkDelete,
  onClearSelection,
  bulkEditor,
  total,
  page = 1,
  pageSize = 50,
  onPageChange,
  onPageSizeChange,
  visibleColumns,
  isDeleting = false,
  isBulkDeleting = false,
  isInteractionDisabled = false,
}: AlfredDevicesTableProps) {
  // If visibleColumns is not provided, show all optional columns
  const col = (key: ColumnKey) => !visibleColumns || visibleColumns.has(key)
  const [pendingDelete, setPendingDelete] = React.useState<AlfredDeviceUI | null>(null)
  // Table adds its own overflow wrapper, so preserve both possible scroll containers.
  const scrollContainerRef = React.useRef<HTMLDivElement>(null)
  const tableRef = React.useRef<HTMLTableElement>(null)
  const pendingSortScrollRef = React.useRef<{ outer: number; inner: number } | null>(null)

  const handleSort = React.useCallback((field: AlfredDeviceSortField) => {
    pendingSortScrollRef.current = {
      outer: scrollContainerRef.current?.scrollLeft ?? 0,
      inner: tableRef.current?.parentElement?.scrollLeft ?? 0,
    }
    onSortRequested(field)
  }, [onSortRequested])
  const onSort = isInteractionDisabled ? undefined : handleSort

  React.useLayoutEffect(() => {
    if (isLoading || !pendingSortScrollRef.current) return

    const { outer, inner } = pendingSortScrollRef.current
    if (scrollContainerRef.current) scrollContainerRef.current.scrollLeft = outer
    if (tableRef.current?.parentElement) tableRef.current.parentElement.scrollLeft = inner
    pendingSortScrollRef.current = null
  }, [devices, isLoading])

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return
    await onDelete?.(pendingDelete)
    setPendingDelete(null)
  }

  const visibleIds = devices.map((d) => d.id)
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id))
  const someVisibleSelected = visibleIds.some((id) => selectedIds.has(id))

  if (isLoading) return <DevicesTableSkeleton />
  if (error) {
    return isSelectedClientNotFound
      ? (
          <ClientNotFoundState
            description="The selected client no longer exists. Clear the filter to continue; the client list is being refreshed."
            actionLabel="Clear client filter"
            onAction={onClearClientFilter}
          />
        )
      : <ApiErrorState onRetry={onRetry} />
  }
  if (devices.length === 0) {
    return hasActiveFilter
      ? <NoSearchResultsEmpty onAction={onClearFilters} />
      : <NoDevicesEmpty onAction={onAddDevice} />
  }

  return (
    <Card className="bg-card border-border">
      <CardContent className="p-0">
        {bulkEditor ? (
          <div className="border-b border-border bg-primary/5 px-4 py-3">
            {bulkEditor}
          </div>
        ) : (
          <BulkSelectionToolbar
            selectedCount={selectedIds.size}
            onEdit={onBulkEdit}
            onDelete={onBulkDelete}
            onClear={onClearSelection}
            isMutating={isInteractionDisabled}
            isDeleting={isBulkDeleting}
          />
        )}
        <div ref={scrollContainerRef} className="table-scroll-x overflow-x-auto">
          <Table ref={tableRef} className="min-w-max w-full">
            <TableHeader>
              <TableRow
                className="border-border hover:bg-card"
                data-tour="sortable-columns"
              >
                <TableHead className="w-10 pl-4">
                  <Checkbox
                    aria-label="Select all devices on this page"
                    checked={allVisibleSelected ? true : someVisibleSelected ? "indeterminate" : false}
                    onCheckedChange={(checked) => onToggleAll?.(visibleIds, checked === true)}
                    disabled={isInteractionDisabled}
                    className="border-input data-[state=checked]:border-primary data-[state=checked]:bg-primary"
                  />
                </TableHead>
                <DeviceHeader label="Device ID" sortKey="deviceId" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                {col("sotiDeviceId") && <DeviceHeader label="SOTI Device ID" sortKey="sotiDeviceId" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                <DeviceHeader label="Client ID" sortKey="clientId" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                {col("venueCode") && <DeviceHeader label="Venue Code" sortKey="venueCode" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("facilityCode") && <DeviceHeader label="Facility Code" sortKey="facilityCode" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("league") && <DeviceHeader label="League" sortKey="league" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("proVenueUser") && <DeviceHeader label="ProVenue User" sortKey="proVenueUser" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("proVenueInstances") && <DeviceHeader label="ProVenue Instances" sortKey="proVenueInstances" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("operatingMode") && <DeviceHeader label="Operating Mode" sortKey="operatingMode" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("areaCode") && <DeviceHeader label="Area Code" sortKey="areaCode" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("deviceCode") && <DeviceHeader label="Device Code" sortKey="deviceCode" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("validationVendor") && <DeviceHeader label="Validation Vendor" sortKey="validationVendor" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("ticketmasterVenueId") && <DeviceHeader label="TM Venue ID" sortKey="ticketmasterVenueId" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("offlineValidationMode") && <DeviceHeader label="Offline Validation" sortKey="offlineValidationMode" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("offlineBatchUploadLimit") && <DeviceHeader label="Offline Batch Limit" sortKey="offlineBatchUploadLimit" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("turnstileMode") && <DeviceHeader label="Turnstile Mode" sortKey="turnstileMode" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("adminModeIdleWindow") && <DeviceHeader label="Admin Idle Window" sortKey="adminModeIdleWindow" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("validDisplayTime") && <DeviceHeader label="Valid Display Time" sortKey="validDisplayTime" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("invalidDisplayTime") && <DeviceHeader label="Invalid Display Time" sortKey="invalidDisplayTime" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("unlockedAccessTime") && <DeviceHeader label="Unlocked Access Time" sortKey="unlockedAccessTime" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("soundValidNumber") && <DeviceHeader label="Sound Valid #" sortKey="soundValidNumber" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("soundInvalidNumber") && <DeviceHeader label="Sound Invalid #" sortKey="soundInvalidNumber" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("soundWarningNumber") && <DeviceHeader label="Sound Warning #" sortKey="soundWarningNumber" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("maxCpuTemperature") && <DeviceHeader label="Max CPU Temp" sortKey="maxCpuTemperature" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("minCpuTemperature") && <DeviceHeader label="Min CPU Temp" sortKey="minCpuTemperature" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("nfcEnabled") && <DeviceHeader label="NFC" sortKey="nfcEnabled" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("exitScanningAllowed") && <DeviceHeader label="Exit Scanning" sortKey="exitScanningAllowed" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("isLockFailSafe") && <DeviceHeader label="Lock Fail Safe" sortKey="isLockFailSafe" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("useSimpleResponse") && <DeviceHeader label="Simple Response" sortKey="useSimpleResponse" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("enableFan") && <DeviceHeader label="Fan" sortKey="enableFan" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("enableHotspot") && <DeviceHeader label="Hotspot" sortKey="enableHotspot" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("wakeLockEnabled") && <DeviceHeader label="Wake Lock" sortKey="wakeLockEnabled" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("enableScanReset") && <DeviceHeader label="Scan Reset" sortKey="enableScanReset" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("enableManualValidationMask") && <DeviceHeader label="Manual Validation Mask" sortKey="enableManualValidationMask" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("displayConfiguration") && <DeviceHeader label="Display Configuration" sortKey="displayConfiguration" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("appleVendors") && <DeviceHeader label="Apple Vendors" sortKey="appleVendors" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("googleVendors") && <DeviceHeader label="Google Vendors" sortKey="googleVendors" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("mainBackgroundColor") && <DeviceHeader label="Main BG Color" sortKey="mainBackgroundColor" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("secondaryBackgroundColor") && <DeviceHeader label="Secondary BG Color" sortKey="secondaryBackgroundColor" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("idleStateColor") && <DeviceHeader label="Idle State Color" sortKey="idleStateColor" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("createdAt") && <DeviceHeader label="Created At" sortKey="createdAt" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("updatedAt") && <DeviceHeader label="Last Updated" sortKey="updatedAt" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                {col("updatedBy") && <DeviceHeader label="Updated By" sortKey="updatedBy" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />}
                <TableHead className="text-muted-foreground font-medium w-24 sticky right-0 bg-card z-10">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {devices.map((device, index) => (
                <TableRow
                  key={device.id}
                  data-tour={index === 0 ? "device-row" : undefined}
                  onClick={() => {
                    if (isInteractionDisabled) return
                    onRowClick?.(device)
                  }}
                  aria-disabled={isInteractionDisabled}
                  className={`border-border transition-colors ${
                    index % 2 === 1 ? "bg-muted/30 hover:bg-muted/50" : "hover:bg-muted/50"
                  } ${!isInteractionDisabled && onRowClick ? "cursor-pointer" : ""} ${
                    selectedIds.has(device.id) ? "bg-primary/10 hover:bg-primary/15" : ""
                  } ${isInteractionDisabled ? "cursor-not-allowed opacity-60" : ""}`}
                >
                  <TableCell
                    className="w-10 pl-4"
                    data-tour={index === 0 ? "bulk-select" : undefined}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <Checkbox
                      aria-label={`Select ${device.deviceId}`}
                      checked={selectedIds.has(device.id)}
                      onCheckedChange={() => onToggleSelect?.(device.id)}
                      disabled={isInteractionDisabled}
                      className="border-input data-[state=checked]:border-primary data-[state=checked]:bg-primary"
                    />
                  </TableCell>
                  <TableCell>
                    <div className="font-medium text-foreground">{device.deviceId}</div>
                  </TableCell>
                  {col("sotiDeviceId") && <TableCell><CellValue k="sotiDeviceId" d={device}>{device.sotiDeviceId}</CellValue></TableCell>}
                  <TableCell>
                    <div className="font-medium text-foreground">{device.clientId}</div>
                  </TableCell>
                  {col("venueCode") && <TableCell><CellValue k="venueCode" d={device}>{device.venueCode}</CellValue></TableCell>}
                  {col("facilityCode") && <TableCell><CellValue k="facilityCode" d={device}>{device.facilityCode}</CellValue></TableCell>}
                  {col("league") && <TableCell><CellValue k="league" d={device}>{device.league}</CellValue></TableCell>}
                  {col("proVenueUser") && <TableCell><CellValue k="proVenueUser" d={device}>{device.proVenueUser}</CellValue></TableCell>}
                  {col("proVenueInstances") && (
                    <TableCell>
                      <ArrayFieldCell
                        count={device.proVenueInstances.length}
                        noun="instance"
                        isDefault={device.inheritedFromGlobal.has("proVenueInstances")}
                        hoverContent={
                          <ArrayHoverList
                            items={device.proVenueInstances.map((i) =>
                              i.name || i.hostName || "Unnamed instance",
                            )}
                          />
                        }
                        editor={
                          <ProVenueInstanceEditor
                            items={device.proVenueInstances.map((i) => ({
                              ...i,
                              consumerKey: "",
                              consumerSecret: "",
                            }))}
                            onChange={() => {}}
                            disabled
                          />
                        }
                      />
                    </TableCell>
                  )}
                  {col("operatingMode") && <TableCell><CellValue k="operatingMode" d={device}>{device.operatingModeLabel}</CellValue></TableCell>}
                  {col("areaCode") && <TableCell><CellValue k="areaCode" d={device}>{device.areaCode}</CellValue></TableCell>}
                  {col("deviceCode") && <TableCell><CellValue k="deviceCode" d={device}>{device.deviceCode}</CellValue></TableCell>}
                  {col("validationVendor") && <TableCell><CellValue k="validationVendor" d={device}>{device.validationVendor}</CellValue></TableCell>}
                  {col("ticketmasterVenueId") && <TableCell><CellValue k="ticketmasterVenueId" d={device}>{device.ticketmasterVenueId}</CellValue></TableCell>}
                  {col("offlineValidationMode") && <TableCell><CellValue k="offlineValidationMode" d={device}><BoolBadge value={device.offlineValidationMode} /></CellValue></TableCell>}
                  {col("offlineBatchUploadLimit") && <TableCell><CellValue k="offlineBatchUploadLimit" d={device}>{device.offlineBatchUploadLimit}</CellValue></TableCell>}
                  {col("turnstileMode") && <TableCell><CellValue k="turnstileMode" d={device}>{device.turnstileMode}</CellValue></TableCell>}
                  {col("adminModeIdleWindow") && <TableCell><CellValue k="adminModeIdleWindow" d={device}>{device.adminModeIdleWindow}</CellValue></TableCell>}
                  {col("validDisplayTime") && <TableCell><CellValue k="validDisplayTime" d={device}>{device.validDisplayTime}</CellValue></TableCell>}
                  {col("invalidDisplayTime") && <TableCell><CellValue k="invalidDisplayTime" d={device}>{device.invalidDisplayTime}</CellValue></TableCell>}
                  {col("unlockedAccessTime") && <TableCell><CellValue k="unlockedAccessTime" d={device}>{device.unlockedAccessTime}</CellValue></TableCell>}
                  {col("soundValidNumber") && <TableCell><CellValue k="soundValidNumber" d={device}>{device.soundValidNumber}</CellValue></TableCell>}
                  {col("soundInvalidNumber") && <TableCell><CellValue k="soundInvalidNumber" d={device}>{device.soundInvalidNumber}</CellValue></TableCell>}
                  {col("soundWarningNumber") && <TableCell><CellValue k="soundWarningNumber" d={device}>{device.soundWarningNumber}</CellValue></TableCell>}
                  {col("maxCpuTemperature") && <TableCell><CellValue k="maxCpuTemperature" d={device}>{device.maxCpuTemperature}</CellValue></TableCell>}
                  {col("minCpuTemperature") && <TableCell><CellValue k="minCpuTemperature" d={device}>{device.minCpuTemperature}</CellValue></TableCell>}
                  {col("nfcEnabled") && <TableCell><CellValue k="nfcEnabled" d={device}><BoolBadge value={device.nfcEnabled} /></CellValue></TableCell>}
                  {col("exitScanningAllowed") && <TableCell><CellValue k="exitScanningAllowed" d={device}><BoolBadge value={device.exitScanningAllowed} /></CellValue></TableCell>}
                  {col("isLockFailSafe") && <TableCell><CellValue k="isLockFailSafe" d={device}><BoolBadge value={device.isLockFailSafe} /></CellValue></TableCell>}
                  {col("useSimpleResponse") && <TableCell><CellValue k="useSimpleResponse" d={device}><BoolBadge value={device.useSimpleResponse} /></CellValue></TableCell>}
                  {col("enableFan") && <TableCell><CellValue k="enableFan" d={device}><BoolBadge value={device.enableFan} /></CellValue></TableCell>}
                  {col("enableHotspot") && <TableCell><CellValue k="enableHotspot" d={device}><BoolBadge value={device.enableHotspot} /></CellValue></TableCell>}
                  {col("wakeLockEnabled") && <TableCell><CellValue k="wakeLockEnabled" d={device}><BoolBadge value={device.wakeLockEnabled} /></CellValue></TableCell>}
                  {col("enableScanReset") && <TableCell><CellValue k="enableScanReset" d={device}><BoolBadge value={device.enableScanReset} /></CellValue></TableCell>}
                  {col("enableManualValidationMask") && <TableCell><CellValue k="enableManualValidationMask" d={device}><BoolBadge value={device.enableManualValidationMask} /></CellValue></TableCell>}
                  {col("displayConfiguration") && (
                    <TableCell>
                      <ArrayFieldCell
                        count={device.displayConfiguration.length}
                        noun="screen"
                        isDefault={device.inheritedFromGlobal.has("displayConfiguration")}
                        hoverContent={
                          <ArrayHoverList
                            items={device.displayConfiguration.map(
                              (item) =>
                                DISPLAY_CONFIGURATION_ITEM_OPTIONS.find((o) => o.value === item)?.label ??
                                item,
                            )}
                          />
                        }
                        editor={
                          <DisplayConfigurationEditor
                            items={device.displayConfiguration}
                            onChange={() => {}}
                            disabled
                          />
                        }
                      />
                    </TableCell>
                  )}
                  {col("appleVendors") && (
                    <TableCell>
                      <ArrayFieldCell
                        count={device.appleVendors.length}
                        noun="credential"
                        isDefault={device.inheritedFromGlobal.has("appleVendors")}
                        hoverContent={
                          <ArrayHoverList
                            items={device.appleVendors.map((v) => v.vendor || v.merchantID || "Unnamed credential")}
                          />
                        }
                        editor={
                          <AppleVendorEditor
                            items={device.appleVendors}
                            onChange={() => {}}
                            disabled
                          />
                        }
                      />
                    </TableCell>
                  )}
                  {col("googleVendors") && (
                    <TableCell>
                      <ArrayFieldCell
                        count={device.googleVendors.length}
                        noun="credential"
                        isDefault={device.inheritedFromGlobal.has("googleVendors")}
                        hoverContent={
                          <ArrayHoverList
                            items={device.googleVendors.map((v) => v.vendor || v.collectorId || "Unnamed credential")}
                          />
                        }
                        editor={
                          <GoogleVendorEditor
                            items={device.googleVendors}
                            onChange={() => {}}
                            disabled
                          />
                        }
                      />
                    </TableCell>
                  )}
                  {col("mainBackgroundColor") && <TableCell><CellValue k="mainBackgroundColor" d={device}>{device.mainBackgroundColor}</CellValue></TableCell>}
                  {col("secondaryBackgroundColor") && <TableCell><CellValue k="secondaryBackgroundColor" d={device}>{device.secondaryBackgroundColor}</CellValue></TableCell>}
                  {col("idleStateColor") && <TableCell><CellValue k="idleStateColor" d={device}>{device.idleStateColor}</CellValue></TableCell>}
                  {col("createdAt") && <TableCell><CellValue k="createdAt" d={device} nowrap>{device.createdAt}</CellValue></TableCell>}
                  {col("updatedAt") && <TableCell><CellValue k="updatedAt" d={device} nowrap>{device.updatedAt}</CellValue></TableCell>}
                  {col("updatedBy") && <TableCell><CellValue k="updatedBy" d={device}>{device.updatedBy}</CellValue></TableCell>}
                  <TableCell className="sticky right-0 bg-card z-10">
                    <div
                      className="flex items-center gap-1"
                      data-tour={index === 0 ? "device-actions" : undefined}
                    >
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={isInteractionDisabled}
                        onClick={(e) => { e.stopPropagation(); onEdit?.(device) }}
                        className="text-muted-foreground hover:text-foreground hover:bg-secondary h-8 w-8"
                      >
                        <Pencil className="h-4 w-4" />
                        <span className="sr-only">Edit device</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={isInteractionDisabled}
                        onClick={(e) => { e.stopPropagation(); onClone?.(device) }}
                        className="text-muted-foreground hover:text-foreground hover:bg-secondary h-8 w-8"
                      >
                        <Copy className="h-4 w-4" />
                        <span className="sr-only">Clone device</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={isInteractionDisabled}
                        onClick={(e) => { e.stopPropagation(); setPendingDelete(device) }}
                        className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 h-8 w-8"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only">Delete device</span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-border gap-4">
          <p className="text-sm text-muted-foreground whitespace-nowrap">
            {(() => {
              const apiTotal = total ?? devices.length
              const start = (page - 1) * pageSize + 1
              const end = Math.min(page * pageSize, apiTotal)
              return `Showing ${start}–${end} of ${apiTotal} device${apiTotal !== 1 ? "s" : ""}`
            })()}
          </p>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground whitespace-nowrap">Rows per page</span>
              <Select
                value={String(pageSize)}
                onValueChange={(v) => onPageSizeChange?.(Number(v))}
                disabled={isInteractionDisabled}
              >
                <SelectTrigger className="w-20 h-8 bg-card border-border text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[25, 50, 100, 200].map((n) => (
                    <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={isInteractionDisabled || page <= 1}
                onClick={() => onPageChange?.(page - 1)}
                className="bg-card border-border text-muted-foreground hover:bg-secondary disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <span className="text-sm text-muted-foreground px-1">Page {page}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={isInteractionDisabled || page * pageSize >= (total ?? devices.length)}
                onClick={() => onPageChange?.(page + 1)}
                className="bg-card border-border text-muted-foreground hover:bg-secondary disabled:opacity-40"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
      <DeleteAlfredDeviceDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => { if (!open && !isDeleting) setPendingDelete(null) }}
        devices={pendingDelete ? [{
          deviceId: pendingDelete.deviceId,
          clientId: pendingDelete.clientId,
        }] : []}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
      />
    </Card>
  )
}

// Renders a sortable header for columns the API can sort by (per the shared
// allow-list) and a plain header for everything else (e.g. array-valued columns
// the backend rejects), so clicking a header can never send an invalid sortBy.
function DeviceHeader({
  label,
  sortKey,
  sortBy,
  sortOrder,
  onSort,
}: {
  label: string
  sortKey: string
  sortBy: AlfredDeviceSortField | null
  sortOrder: SortOrder
  onSort?: (field: AlfredDeviceSortField) => void
}) {
  if (isAlfredDeviceSortField(sortKey)) {
    return (
      <SortableHeader
        label={label}
        sortKey={sortKey}
        currentSortColumn={sortBy}
        currentSortDirection={sortOrder}
        onSort={onSort ?? (() => {})}
        disabled={!onSort}
      />
    )
  }
  return <TableHead className="text-muted-foreground font-medium">{label}</TableHead>
}

function CellValue({
  k,
  d,
  children,
  nowrap,
}: {
  k: string
  d: AlfredDeviceUI
  children: React.ReactNode
  nowrap?: boolean
}) {
  const isDefault = d.inheritedFromGlobal.has(k)
  return (
    <div className={`font-medium text-foreground flex items-center gap-1.5 ${nowrap ? "whitespace-nowrap" : ""}`}>
      {children}
      {isDefault && <span className="text-xs text-muted-foreground">(default)</span>}
    </div>
  )
}

function BoolBadge({ value }: { value: boolean }) {
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
        value ? "bg-success/20 text-success" : "bg-muted text-muted-foreground"
      }`}
    >
      {value ? "Enabled" : "Disabled"}
    </span>
  )
}
